import React from 'react';
import {Subtract} from 'utility-types';

import debounce from 'lodash/debounce';
import { RouteComponentProps } from 'react-router-dom';
import _get from 'lodash/get';
import queryString from 'query-string';
import ExportContextHolder from '../../../../lib/ExportContextHolder';
import {ColumnDefinition} from './common';
import i18n from '../../../../locale/i18n';
import RestClientInstance from "../../../../lib/RestClient";

const t = i18n.t.bind(i18n);

export interface DataService {
  loadPage(page: number, sortColumn: string | undefined, sortOrder: 'ASC' | 'DESC' | undefined, filter: { [key: string]: any }, size?: number): Promise<any>;
}

export interface AdditionalDataServiceProps {
    dataToLoad : {[key: string]: {uri: string, params?: any}};

}

export interface AdditionalDataServiceComponentProps {
    additionalData: any;
}

export interface AdditionalDataServiceComponentState {
  data: any;
}


export const withAdditionalDataServices = <WrappedComponentProps extends AdditionalDataServiceComponentProps & object>(WrappedComponent: React.ComponentType<WrappedComponentProps>) =>
    class extends React.Component<Subtract<WrappedComponentProps, AdditionalDataServiceComponentProps> & AdditionalDataServiceProps, AdditionalDataServiceComponentState> {

        dataToLoad: {[key: string]: {uri: string, params?: any}};

        constructor(props: Readonly<Subtract<WrappedComponentProps, AdditionalDataServiceComponentProps> & AdditionalDataServiceProps>) {
            super(props);

            this.state = {data: {}};

            this.dataToLoad = props.dataToLoad;
        }

        componentDidMount() {

            for (const [key, dateToLoad] of Object.entries(this.dataToLoad)) {

                RestClientInstance.get(dateToLoad.uri).then(
                    (loadedData)=> {

                      let data = this.state.data;
                      data[key] = loadedData.data;


                        this.setState({
                            data: data
                        });
                    }

                );


            }

        }

        render() {
            const {...props} = this.props;

            return <WrappedComponent
                {...props as WrappedComponentProps}
                additionalData={this.state.data}
            />
        }
    };

export interface InfinityScrollComponentProps {
  data: any[];
  page: number;
  totalPages: number;
  totalItems: number;
  loading: boolean;
  loadingMore: boolean;
  loadingInitial: boolean;

  loadMore(): void;
}

interface InfinityScrollProps {
  dataService: DataService;
  sortColumn?: string;
  sortDirection?: 'ASC' | 'DESC';
  filterObject?: object;
}
interface InfinityScrollHoCState {

  loading: boolean;
  loadingMore: boolean;
  page: number;
  totalItems: number;
  totalPages: number;
  data: any[];
}

export const withInfinityScrollHandlers = <WrappedComponentProps extends InfinityScrollComponentProps & object>(WrappedComponent: React.ComponentType<WrappedComponentProps>) =>
    class extends React.Component<Subtract<WrappedComponentProps, InfinityScrollComponentProps> & InfinityScrollProps, InfinityScrollHoCState> {
      _loading: boolean = false;

      constructor(props: Readonly<Subtract<WrappedComponentProps, InfinityScrollComponentProps> & InfinityScrollProps>) {
        super(props);
        this.state = {
          loading: false,
          loadingMore: false,
          page: -1,
          totalItems: 0,
          totalPages: 0,
          data: []
        };

        this.loadMore = this.loadMore.bind(this);
        this.debouncedReload = debounce(this.debouncedReload.bind(this), 1250);
      }

      componentDidMount(): void {
        this.loadPage(0);
      }

      fetchData(page: number) {
        return this.props.dataService.loadPage(page, this.props.sortColumn, this.props.sortDirection, this.props.filterObject || {});
      }

      componentDidUpdate(prevProps: Readonly<Subtract<WrappedComponentProps, InfinityScrollComponentProps> & InfinityScrollProps>): void {
        if (prevProps.sortColumn !== this.props.sortColumn || prevProps.sortDirection !== this.props.sortDirection) {
          this.loadPage(0); // reset even in infinity mode
        }
        if (prevProps.filterObject !== this.props.filterObject) {
          this.debouncedReload();
        }
      }

      debouncedReload() {
        this.loadPage(0);
      }

      loadMore() {
        const page = this.state.page + 1;
        if (this._loading) {
          return;
        }
        this._loading = true;
        this.setState({
          loadingMore: true,
          loading: page === 0 // only show full loading state when initial data is loaded
        }, () => {
          this.fetchData(page)
          .then(response => {
            this._loading = false;
            this.setState(prevState => {
              if (response.data && response.data.data && response.data.data.body) {
                // old response format...
                return {
                  data: [...prevState.data, ...response.data.data.body.content],
                  loading: false,
                  loadingMore: false,
                  page: response.data.data.body.number,
                  totalItems: response.data.data.body.totalElements,
                  totalPages: response.data.data.body.totalPages,
                }
              } else {
                return {
                  data: [...prevState.data, ...response.data.content],
                  loading: false,
                  loadingMore: false,
                  page: response.data.number,
                  totalItems: response.data.totalElements,
                  totalPages: response.data.totalPages,
                }
              }
            });
          });
        })
      }

      // TODO cancel pending "load more" request when reloading entire page 
      // TODO cancel current request instead of just returning => user wants to load another page
      loadPage(page: number) {
        if (this._loading) {
          return;
        }
        this._loading = true;
        this.setState({
          loading: true
        }, () => {
          this.fetchData(page)
          .then(response => {
            this._loading = false;
            this.setState(prevState => {
              if (response.data && response.data.data && response.data.data.body) {
                // old response format...
                return {
                  data: response.data.data.body.content,
                  loading: false,
                  loadingMore: false,
                  page: response.data.data.body.number,
                  totalItems: response.data.data.body.totalElements,
                  totalPages: response.data.data.body.totalPages,
                }
              } else {
                return {
                  data: response.data.content,
                  loading: false,
                  loadingMore: false,
                  page: response.data.number,
                  totalItems: response.data.totalElements,
                  totalPages: response.data.totalPages,
                }
              }
            });
          });
        })
      }

      render() {
        const {...props} = this.props;

        return <WrappedComponent
            {...props as WrappedComponentProps}
            loadingInitial={this.state.loading && this.state.page === -1}
            loading={this.state.loading}
            loadingMore={this.state.loadingMore}
            data={this.state.data}
            totalItems={this.state.totalItems}
            totalPages={this.state.totalPages}
            page={this.state.page}
            loadMore={this.loadMore}/>
      }
    }
;

export interface ColumnSelectableComponentProps {
  selectedColumns: string[];

  updateSelectedColumns(columns: string[]): void;
}

interface ColumnSelectableProps {
  defaultColumns: string[];

  // can be optionally provided - TODO extract types to common interface
  viewSettings?: { [key: string]: { [key: string]: any } };

  saveViewSettings?(key: string, value: { [key: string]: any }): void;
}

interface ColumnSelectableState {
  selectedColumns: string[];
}

export const withColumnSelection = <WrappedComponentProps extends ColumnSelectableComponentProps & object>(WrappedComponent: React.ComponentType<WrappedComponentProps>) =>
    class extends React.Component<Subtract<WrappedComponentProps, ColumnSelectableComponentProps> & ColumnSelectableProps, ColumnSelectableState> {

      constructor(props: Readonly<Subtract<WrappedComponentProps, ColumnSelectableComponentProps> & ColumnSelectableProps>) {
        super(props);

        const selectedColumns = props.viewSettings && props.viewSettings.selectedColumns && Array.isArray(props.viewSettings.selectedColumns)
            ? props.viewSettings.selectedColumns
            : props.defaultColumns;

        this.state = {
          selectedColumns: selectedColumns
        };

        this.updateSelectedColumns = this.updateSelectedColumns.bind(this);
      }

      updateSelectedColumns(columns: string[]) {
        this.setState({
          selectedColumns: columns
        }, () => {
          if (this.props.saveViewSettings) {
            this.props.saveViewSettings('selectedColumns', this.state.selectedColumns)
          }
        }); // TODO save selection and load on mount
      }

      render() {
        const {defaultColumns, ...props} = this.props;
        return (
            <WrappedComponent
                {...props as WrappedComponentProps}
                selectedColumns={this.state.selectedColumns}
                updateSelectedColumns={this.updateSelectedColumns}
            />
        );
      }
    };


export interface ColumnSortableComponentProps {
  sortColumn: string;
  sortDirection: 'ASC' | 'DESC';

  handleSortChange(column: string): void;
  handleSortUpdate(column: string, direction: 'ASC' | 'DESC'): void;
}

interface ColumnSortableProps extends Partial<RouteComponentProps> {
  defaultSortColumn: string;
  defaultSortDirection: 'ASC' | 'DESC';
}

interface ColumnSortableState {
  sortColumn: string;
  sortDirection: 'ASC' | 'DESC';
}

export const withColumnSort = <WrappedComponentProps extends ColumnSortableComponentProps & object>(WrappedComponent: React.ComponentType<WrappedComponentProps>) =>
    class extends React.Component<Subtract<WrappedComponentProps, ColumnSortableComponentProps> & ColumnSortableProps, ColumnSortableState> {

      constructor(props: Readonly<Subtract<WrappedComponentProps, ColumnSortableComponentProps> & ColumnSortableProps>) {
        super(props);

        let previousSort = {} as {sortColumn?: string; sortDirection?: 'ASC' | 'DESC';};

        if(props && props.location && props.location.search && props.location.search.length > 0) {
          console.log(props.location.search);
          const queryParsed = queryString.parse(props.location.search);
          if(queryParsed.sortColumn) {
            previousSort.sortColumn = queryParsed.sortColumn;
          }
          if(queryParsed.sortDirection) {
            previousSort.sortDirection = queryParsed.sortDirection;
          }
        }

        this.state = {
          sortColumn: previousSort.sortColumn || props.defaultSortColumn,
          sortDirection: previousSort.sortDirection || props.defaultSortDirection
        };

        this.handleSortChange = this.handleSortChange.bind(this);
        this.handleSortUpdate = this.handleSortUpdate.bind(this);
        this.saveSortState = this.saveSortState.bind(this);
      }

      saveSortState() {
          if(this.props.history && this.props.location) {
            const currentQueryParams = queryString.parse(this.props.location.search);
            currentQueryParams.sortColumn = this.state.sortColumn;
            currentQueryParams.sortDirection = this.state.sortDirection;

            this.props.history.replace({
              pathname: this.props.history.location.pathname,
              search: queryString.stringify(currentQueryParams)
            });
          }
      }

      // Toggle Sort
      handleSortChange(column: string) {
        if (this.state.sortColumn === column) {
          this.setState({sortColumn: column, sortDirection: (this.state.sortDirection === "ASC" ? "DESC" : "ASC")}, this.saveSortState);
        } else {
          this.setState({sortColumn: column, sortDirection: "ASC"}, this.saveSortState);
        } // TODO save selection and load on mount
      }

      // Set Sort
      handleSortUpdate(column: string, direction: 'ASC' | 'DESC') {
        this.setState({sortColumn: column, sortDirection: direction}, this.saveSortState);
      }

      render() {
        const {...props} = this.props;
        return (
            <WrappedComponent
                {...props as WrappedComponentProps}
                sortColumn={this.state.sortColumn}
                sortDirection={this.state.sortDirection}
                handleSortChange={this.handleSortChange}
                handleSortUpdate={this.handleSortUpdate}
            />
        );
      }
    };


export interface FilterObjectComponentProps {
  filterObject: { [name: string]: any };

  handleFilterChange(filterName: string, value: any): void;
}

interface FilterObjectProps extends Partial<RouteComponentProps> {
  defaultFilter?: { [name: string]: any };
}

interface FilterObjectState {
  filterObject: { [name: string]: any };
}

// TODO keep filters in URL for bookmarking + back-button support
export const withFilterObject = <WrappedComponentProps extends FilterObjectComponentProps & object>(WrappedComponent: React.ComponentType<WrappedComponentProps>) =>
    class extends React.Component<Subtract<WrappedComponentProps, FilterObjectComponentProps> & FilterObjectProps, FilterObjectState> {

      constructor(props: Readonly<Subtract<WrappedComponentProps, FilterObjectComponentProps> & FilterObjectProps>) {
        super(props);

        let initialFilters: {[key:string]: any} = {};
        // TODO refactor
        if(props && props.location && props.location.search && props.location.search.length > 0) {
          console.log(props.location.search);
          const queryParsed = queryString.parse(props.location.search);
          if(queryParsed.filter) {
            try {
              initialFilters = JSON.parse(queryParsed.filter);
            } catch(e) {
              // do nothing, invalid filter
            }
          }
        }

        if(props.defaultFilter) {
          // add missing default filter values to initialFilters
          const defaultFilterKeys = Object.keys(props.defaultFilter);
          for(let key of defaultFilterKeys) {
            if(typeof initialFilters[key] === 'undefined') {
              initialFilters[key] = props.defaultFilter[key];
            }
          }
        }

        this.state = {
          filterObject: initialFilters
        };

        this.handleFilterChange = this.handleFilterChange.bind(this);
      }

      handleFilterChange(name: string, value: any) {
        this.setState(prevState => {
          return {
            filterObject: {...prevState.filterObject, [name]: value}
          };
        }, () => {
          if(this.props.history && this.props.location) {
            const currentQueryParams = queryString.parse(this.props.location.search);
            currentQueryParams.filter = JSON.stringify(this.state.filterObject);

            this.props.history.replace({
              pathname: this.props.history.location.pathname,
              search: queryString.stringify(currentQueryParams)
            });
          }
        });
      }

      render() {
        const {...props} = this.props;
        return (
            <WrappedComponent
                {...props as WrappedComponentProps}
                filterObject={this.state.filterObject}
                handleFilterChange={this.handleFilterChange}
            />
        );
      }
    };


export interface ViewSettingsComponentProps {
  viewSettings: { [key: string]: any };

  saveViewSettings(key: string, value: any): void;
}

interface ViewSettingsProps {
  viewName: string;
}

interface ViewSettingsState {
  viewSettings: { [key: string]: { [key: string]: any } };
}

// TODO replace local storage with api calls to /user-settings
export const withViewSettings = <WrappedComponentProps extends ViewSettingsComponentProps & object>(WrappedComponent: React.ComponentType<WrappedComponentProps>) =>
    class extends React.Component<Subtract<WrappedComponentProps, ViewSettingsComponentProps> & ViewSettingsProps, ViewSettingsState> {

      constructor(props: Readonly<Subtract<WrappedComponentProps, ViewSettingsComponentProps> & ViewSettingsProps>) {
        super(props);

        const retrievedSettingsJson = localStorage.getItem(props.viewName);
        const retrievedSettings = retrievedSettingsJson != null ? JSON.parse(retrievedSettingsJson) : {};

        this.state = {
          viewSettings: retrievedSettings
        };

        this.saveViewSettings = this.saveViewSettings.bind(this);
      }

      saveViewSettings(key: string, value: any) {
        this.setState(prevState => {
          return {
            viewSettings: {
              ...prevState.viewSettings,
              [key]: value
            }
          }
        }, () => {
          localStorage.setItem(this.props.viewName, JSON.stringify(this.state.viewSettings));
        });
      }

      render() {
        const {...props} = this.props;
        return (
            <WrappedComponent
                {...props as WrappedComponentProps}
                viewSettings={this.state.viewSettings}
                saveViewSettings={this.saveViewSettings}
            />
        );
      }
    };


export interface PrintAndExportComponentProps {
}

interface PrintAndExportProps {
  dataService: DataService;
  sortColumn?: string;
  sortDirection?: 'ASC' | 'DESC';
  filterObject?: object;
  printColumns: ColumnDefinition[];
  printPath?: string[];
}

interface PrintAndExportState {
}

export const withPrintAndExport = <WrappedComponentProps extends PrintAndExportComponentProps & object>(WrappedComponent: React.ComponentType<WrappedComponentProps>) =>
    class extends React.Component<Subtract<WrappedComponentProps, PrintAndExportComponentProps> & PrintAndExportProps, PrintAndExportState> {

      constructor(props: Readonly<Subtract<WrappedComponentProps, PrintAndExportComponentProps> & PrintAndExportProps>) {
        super(props);

        this.state = {
        };

        this.exportDataV2 = this.exportDataV2.bind(this);
      }

      componentDidMount(): void {
        ExportContextHolder.enabled = true;
        ExportContextHolder.loadFunctionV2 = this.exportDataV2;
        ExportContextHolder.routeData = this.props.printPath ? this.props.printPath as string[] : null;
        ExportContextHolder.fields = this.props.printColumns.map(c => c.labelTranslationKey? t(c.labelTranslationKey) : c.label);
        ExportContextHolder.reloadHeaderFunc();
      }

      componentWillUnmount(): void {
        ExportContextHolder.reset();
      }

      exportDataV2(page: number, size: number): Promise<{page: number; totalPages: number; rows: any[]}> {
        const mapRows = (objectRows: any[]) => {
          let result = [];
          for(let objectRow of objectRows) {
            let arrayRow = [];

            let columnsToLoad = ExportContextHolder.columns && ExportContextHolder.columns.length > 0 ? ExportContextHolder.columns : this.props.printColumns;
            for(let column of columnsToLoad) {
              const cellContent = column.printExportRender ? column.printExportRender(_get(objectRow, column.fieldName), objectRow, this.props.filterObject) : _get(objectRow, column.fieldName);
              arrayRow.push(cellContent || ''); // TODO support custom renderers
            }
            result.push(arrayRow);
          }
          return result;
        };
        return new Promise((resolve, reject) => {
          this.props.dataService.loadPage(page, this.props.sortColumn, this.props.sortDirection, this.props.filterObject || {}, size)
              .then(response => {
                if (response.data && response.data.data && response.data.data.body) {
                  // old response format...
                  resolve({
                    page: response.data.data.body.number,
                    totalPages: response.data.data.body.totalPages,
                    rows: mapRows(response.data.data.body.content)
                  });
                } else {
                  resolve({
                    page: response.data.number,
                    totalPages: response.data.totalPages,
                    rows: mapRows(response.data.content)
                  });
                }
              }).catch((err) => reject(err));
        });
      }


      render() {
        const {...props} = this.props;
        return (
            <WrappedComponent
                {...props as WrappedComponentProps}
            />
        );
      }
    };


